import { createWithEqualityFn } from 'zustand/traditional'
import {
  SubscriptionData,
  type SubscriptionInfo, // Import as type
} from 'app/data/subscriptions/subscriptionsApi'
import { fetchCurrentSubscription as fetchCurrentSubscriptionApi } from 'app/data/subscriptions/subscriptionsApi' // Renamed for clarity

// Re-export the type

export type { SubscriptionInfo }
import { createJSONStorage, persist } from 'zustand/middleware'
import { useAxiosClient } from 'app/data/api'
import { useTimezone } from 'app/data/user/useUser'
import { AxiosError, AxiosInstance } from 'axios'
import type { Session } from '@supabase/supabase-js'
import { useCallback, useEffect } from 'react'
import { jwtDecode, JwtPayload as DecodedJwtPayload } from 'jwt-decode' // Renamed to avoid conflict
import { storage } from 'app/shared/utils/storage'
import { useSessionContext } from 'app/utils/supabase/useSessionContext'

// Extend the JwtPayload interface to include app_metadata
declare module 'jwt-decode' {
  interface JwtPayload {
    app_metadata?: {
      is_pending_deletion?: boolean
      has_access?: boolean
      is_payment_exempt?: boolean
      [key: string]: any
    }
  }
}

export interface SubscriptionState {
  subscriptionData: SubscriptionData | null
  subscriptionInfo: SubscriptionInfo | null
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  hasPaymentIssue: boolean
  fetchSubscription: (axiosClient: AxiosInstance, timezone: string, session: Session | null) => Promise<void>
  refreshSubscription: (axiosClient: AxiosInstance, timezone: string, session: Session | null) => Promise<void>
  clearStore: () => void
}

// Create a storage object for the web
const oStorage = {
  getItem: async (name: string) => {
    return storage.getItem(name)
  },
  setItem: async (name: string, value: string) => {
    return storage.setItem(name, value)
  },
  removeItem: async (name: string) => {
    return storage.removeItem(name)
  },
}

export const useSubscriptionStore = createWithEqualityFn<SubscriptionState>()(
  persist(
    (set, get) => {
    const isProBonoUserInternal = (session: Session | null): boolean => {
      if (session?.access_token) {
        try {
          // Use the augmented JwtPayload type directly
          const decoded = jwtDecode<DecodedJwtPayload>(session.access_token)
          return decoded?.app_metadata?.is_payment_exempt === true
        } catch (error) {
          console.error('[SubscriptionStore] Error decoding JWT token for pro-bono check:', error)
          return false
        }
      }
      return false
    }

    // Helper to fetch raw subscription data - uses the imported fetchCurrentSubscriptionApi
    // This function is now simpler as the original fetchCurrentSubscription from subscriptionsApi.ts
    // already handles timezone conversion and basic validation.
    const fetchSubscriptionDataInternal = async (
      axiosClient: AxiosInstance,
      timezone: string
    ): Promise<SubscriptionData> => {
      // The call to fetchCurrentSubscriptionApi will use the passed axiosClient and timezone.
      // It already handles timezone conversions and throws errors on failure.
      return await fetchCurrentSubscriptionApi(axiosClient, timezone)
    }

    const deriveSubscriptionInfoInternal = (
      data: SubscriptionData | null,
      isProBono: boolean,
      sessionForEdgeCases: Session | null // For re-checking pro-bono status within specific subscription states
    ): SubscriptionInfo | null => {
      // Calculate autoRenewDate for all cases
      let autoRenewDate: string | undefined = undefined

      if (data) {
        if (data.renews_at) {
          const date = new Date(data.renews_at)
          autoRenewDate = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        } else if (data.expires_at) {
          const date = new Date(data.expires_at)
          autoRenewDate = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        }
      }

      if (isProBono) {
        return {
          isOnFreeTrial: false,
          daysRemaining: 0,
          planType: 'Pro Bono',
          isProBono: true,
          source: data?.source, // Pro-bono might still have a source if they had a previous subscription
          autoRenewDate, // Include autoRenewDate (will be undefined for pro-bono if data is null)
        }
      }

      if (!data) return null

      const now = new Date()
      let daysRemaining = 0
      let planType = 'Unknown'
      let isOnFreeTrial = false

      // Re-check pro-bono for users who might have a subscription record but are exempt
      if (data.status === 'trial') {
        if (isProBonoUserInternal(sessionForEdgeCases)) {
          return {
            isOnFreeTrial: false,
            daysRemaining: 0,
            planType: 'Pro Bono',
            isProBono: true,
            source: data.source,
            autoRenewDate,
          }
        }
        isOnFreeTrial = true
        if (data.trial_end_at) {
          const trialEndDate = new Date(data.trial_end_at)
          daysRemaining = Math.max(
            0,
            Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          )
        }
        planType = 'Free Trial'
      } else if (data.status === 'active') {
        if (isProBonoUserInternal(sessionForEdgeCases)) {
          return {
            isOnFreeTrial: false,
            daysRemaining: 0,
            planType: 'Pro Bono',
            isProBono: true,
            source: data.source,
            autoRenewDate,
          }
        }
        isOnFreeTrial = false
        // daysRemaining for active subscriptions is based on expires_at, which is also used for autoRenewDate.
        // If expires_at is present, daysRemaining will be calculated.
        // If only renews_at is present, daysRemaining might not be accurate here without further logic.
        // However, the primary concern is autoRenewDate.
        if (data.expires_at) {
          const expiryDate = new Date(data.expires_at)
          daysRemaining = Math.max(
            0,
            Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          )
        }
        planType = data.plan_id
          ? data.plan_id.toLowerCase().includes('month')
            ? 'Monthly'
            : 'Yearly'
          : 'Subscription'
      } else if (data.status === 'cancelled') {
        if (isProBonoUserInternal(sessionForEdgeCases)) {
          return {
            isOnFreeTrial: false,
            daysRemaining: 0,
            planType: 'Pro Bono',
            isProBono: true,
            source: data.source,
            autoRenewDate,
          }
        }
        isOnFreeTrial = false
        if (data.expires_at) {
          // Access might continue until expires_at
          const expiryDate = new Date(data.expires_at)
          daysRemaining = Math.max(
            0,
            Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          )
        }
        planType = 'Cancelled'
      }

      return {
        isOnFreeTrial,
        daysRemaining,
        planType,
        isProBono: false, // Explicitly false if not caught by initial pro-bono check
        source: data.source,
        autoRenewDate, // Include the calculated autoRenewDate
      }
    }

    return {
      subscriptionData: null,
      subscriptionInfo: null,
      isLoading: true,
      isFetching: false,
      error: null,
      hasPaymentIssue: false,

      fetchSubscription: async (axiosClient: AxiosInstance, timezone: string, session: Session | null) => {
        console.log('[SubscriptionStore] Fetching subscription information', axiosClient, timezone, session)
        // Check if already fetching to prevent race conditions
        const currentState = get()
        if (currentState.isFetching) {
          console.log('[SubscriptionStore] Already fetching, skipping duplicate call')
          return
        }
        console.log('[SubscriptionStore] Setting loading state to true')
        set({ isFetching: true, error: null })

        try {
          const initialProBono = isProBonoUserInternal(session) // Pass the placeholder session
          let currentSubscriptionData: SubscriptionData | null = null
          let derivedInfo: SubscriptionInfo | null = null
          let paymentIssue = false

          if (initialProBono) {
            console.log('[SubscriptionStore] Setting derivedInfo to Pro Bono')
            derivedInfo = deriveSubscriptionInfoInternal(null, true, session)
            paymentIssue = false
          } else {
            try {
              console.log('[SubscriptionStore] Fetching subscription data')
              // Use the passed axiosClient and timezone
              currentSubscriptionData = await fetchSubscriptionDataInternal(axiosClient, timezone)
              console.log('[SubscriptionStore] Deriving subscription info')
              derivedInfo = deriveSubscriptionInfoInternal(currentSubscriptionData, false, session)
              paymentIssue = currentSubscriptionData?.has_billing_issue || false
            } catch (fetchError) {
              console.log('[SubscriptionStore] Error fetching subscription data', fetchError)
              if (fetchError instanceof AxiosError && fetchError.response?.status === 404) {
                console.warn(
                  '[SubscriptionStore] No subscription record found (404). Treating as Unknown.'
                )
                derivedInfo = {
                  isOnFreeTrial: false,
                  daysRemaining: 0,
                  planType: 'Unknown',
                  isProBono: false,
                  source: undefined,
                }
                paymentIssue = false
              } else {
                throw fetchError
              }
            }
          }

          console.log('[SubscriptionStore] Setting loading state to false')
          set({
            subscriptionData: currentSubscriptionData,
            subscriptionInfo: derivedInfo,
            isLoading: false,
            isFetching: false,
            error: null,
            hasPaymentIssue: paymentIssue,
          })
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err))
          set({
            error,
            isLoading: false,
            isFetching: false,
            subscriptionData: null,
            subscriptionInfo: null,
            hasPaymentIssue: false,
          })
        }
      },

      refreshSubscription: async (axiosClient: AxiosInstance, timezone: string, session: Session | null) => {
        console.log('[SubscriptionStore] Refreshing subscription information', axiosClient, timezone, session)
        // Now calls fetchSubscription with the required parameters
        await get().fetchSubscription(axiosClient, timezone, session)
      },

      clearStore: () => {
        set(
          {
            subscriptionData: null,
            subscriptionInfo: null,
            isLoading: true,
            isFetching: false,
            error: null,
            hasPaymentIssue: false,
          }
        )
      },
    }
  },
  {
    name: 'subscription-storage',
    storage: createJSONStorage(() => oStorage),
  }
)
)

/**
 * Hook to get subscription information and related actions.
 * Automatically fetches subscription data if not already loaded.
 * @returns An object containing `subscriptionInfo`, `isLoading`, `error`, and `refreshSubscription`.
 */
export const useSubscriptionInfo = () => {
  const storeSubscriptionInfo = useSubscriptionStore((state) => state.subscriptionInfo)
  const hasPaymentIssue = useSubscriptionStore((state) => state.hasPaymentIssue)
  const isLoading = useSubscriptionStore((state) => state.isLoading)
  const isFetching = useSubscriptionStore((state) => state.isFetching)
  const error = useSubscriptionStore((state) => state.error)
  const storeFetchSubscription = useSubscriptionStore((state) => state.fetchSubscription)
  const storeRefreshSubscription = useSubscriptionStore((state) => state.refreshSubscription)

  const axiosClient = useAxiosClient()
  const { timezone } = useTimezone()
  const { session } = useSessionContext() // Get the real session

  // Automatically fetch subscription data if not loaded and dependencies are available
  useEffect(() => {
    console.log('[useSubscriptionInfo] Auto-fetch effect triggered:', {
      hasSessionToken: !!session?.access_token,
      hasValidData: storeSubscriptionInfo !== null && storeSubscriptionInfo !== undefined,
      isFetching,
      hasError: !!error,
      errorDetails: error,
      hasTimezone: !!timezone,
      timestamp: new Date().toISOString()
    })

    if (!session?.access_token) {
      console.log('[useSubscriptionInfo] No session token, skipping auto-fetch')
      return
    }

    // Don't fetch if we already have subscription info and no error
    const hasValidData = storeSubscriptionInfo !== null && storeSubscriptionInfo !== undefined
    if (hasValidData && !error) {
      console.log('[useSubscriptionInfo] Has valid data and no error, skipping auto-fetch')
      return
    }

    const condition2 = !(isFetching === true)
    // Handle case where error might be an empty object or actual error
    const condition3 = error === null || error === undefined || (error && typeof error === 'object' && Object.keys(error).length === 0)
    const condition4 = !!timezone
    const shouldFetch =  condition2 && condition3 && condition4

    console.log('[useSubscriptionInfo] Auto-fetch conditions:', {
      condition2_notFetching: condition2,
      condition3_noError: condition3,
      condition4_hasTimezone: condition4,
      shouldFetch
    })

    if (shouldFetch) {
      console.log('[useSubscriptionInfo] Auto-fetching subscription data')
      storeFetchSubscription(axiosClient, timezone, session).catch((err) => {
        console.error('[useSubscriptionInfo] Auto-fetch failed:', err)
      })
    }
  }, [axiosClient, timezone, session, isFetching, error, storeSubscriptionInfo])

  const refreshSubscription = useCallback(async () => {
    if (!timezone) {
      console.error('[useSubscriptionInfo] Timezone not available for refresh.')
      throw new Error('Timezone not available for refresh')
    }
    if (!axiosClient) {
      console.error('[useSubscriptionInfo] Axios client not available for refresh.')
      throw new Error('Axios client not available for refresh')
    }

    console.log('[useSubscriptionInfo] Refreshing subscription information', axiosClient, timezone, session)
    await storeRefreshSubscription(axiosClient, timezone, session)
    console.log('[useSubscriptionInfo] Subscription information refreshed')
  }, [storeRefreshSubscription, axiosClient, timezone, session])

  return {
    hasPaymentIssue,
    subscriptionInfo: storeSubscriptionInfo,
    isLoading,
    error,
    refreshSubscription, // Only refresh is exposed - fetching is automatic
  }
}

/**
 * Hook to access the subscription store clear method.
 * Use this to reset the subscription store when the user logs out.
 * @returns An object containing `clearSubscriptionStore`.
 */
export const useSubscriptionStoreClear = () => {
  const clearStore = useSubscriptionStore((state) => state.clearStore)

  return {
    clearSubscriptionStore: clearStore,
  }
}
